import type { App, ComponentPublicInstance, Plugin } from "vue";

import { NetworkException, PermissionException } from "@byloth/core";
import { HandledException, HandlerBuilder } from "@byloth/exceptions";
import type { DismissibleAlert } from "@byloth/vuert";

import { ProjectNotFoundException } from "@/exceptions";
import * as GraphQLExceptions from "@/services/graphql/exceptions";

import useUiStore from "@/stores/ui";
import useUserStore from "@/stores/user";

const ERROR_ALERT: DismissibleAlert = {
    type: "error",
    icon: "x-circle",
    title: "Oh, oh...",
    message: "Something went wrong; please, reload the page.",
    dismissible: true
};

const errorsHandler: Plugin = {
    install(app: App, ...options: undefined[]): void
    {
        const { $router, $vuert } = app.config.globalProperties;

        const _gotoLogin = () =>
        {
            const route = $router.currentRoute.value;

            return $router.replace({
                name: "user-log_in",
                query: { next: route.path }
            });
        };
        const _logOut = () =>
        {
            const $user = useUserStore();

            $user.clear();
        };

        const _handler = new HandlerBuilder()
            .on(ProjectNotFoundException, (exc) =>
            {
                const $ui = useUiStore();

                $vuert.emit({
                    type: "warning",
                    icon: "zap-off",
                    title: "Project not found!",
                    message: exc.message,
                    dismissible: true
                });

                const projectId = $ui.allProjects[0].id as number;

                $router.push({ params: { id: projectId } });

                return new HandledException(exc);
            })
            .on(GraphQLExceptions.AuthenticationException, (exc) =>
            {
                let messageAppendix: string;

                if (exc.forceLogout) { _logOut(); }
                if (exc.gotoLogin)
                {
                    _gotoLogin();

                    messageAppendix = "Please, log in again before continuing.";
                }
                else
                {
                    messageAppendix = "Please, try again.";
                }

                $vuert.emit({
                    type: "error",
                    icon: "user-x",
                    title: "Authentication error!",
                    message: `${exc.message} ${messageAppendix}`,
                    dismissible: true
                });

                return new HandledException(exc);
            })
            .on(GraphQLExceptions.AuthorizationException, (exc) =>
            {
                if (exc.gotoLogin) { _gotoLogin(); }

                $vuert.emit({
                    type: "error",
                    icon: "lock",
                    title: "Authorization error!",
                    message: `${exc.message} Please, log in before continuing.`,
                    dismissible: true
                });

                return new HandledException(exc);
            })
            .on(GraphQLExceptions.TooManyRequestsException, (exc) =>
            {
                $vuert.emit({
                    type: "error",
                    icon: "shield",
                    title: "Too many requests!",
                    message: `${exc.message} Please, try again later.`,
                    dismissible: true
                });

                return new HandledException(exc);
            })
            .on(NetworkException, (exc) =>
            {
                $vuert.emit({
                    type: "error",
                    icon: "link",
                    title: "Network error!",
                    message: `${exc.message} Please, try again later.`,
                    dismissible: true
                });

                return new HandledException(exc);
            })
            .on(PermissionException, (exc) =>
            {
                $vuert.emit({
                    type: "error",
                    icon: "lock",
                    title: "Insufficient permissions!",
                    message: exc.message,
                    dismissible: true
                });

                return new HandledException(exc);
            })
            .default((exc) =>
            {
                // eslint-disable-next-line no-console
                console.error(exc);

                $vuert.emit(ERROR_ALERT);
            });

        app.config.errorHandler = (error: unknown, instance: ComponentPublicInstance | null, info: string) =>
        {
            const result = _handler.handle(error);
            if (result instanceof HandledException)
            {
                // eslint-disable-next-line no-console
                console.warn(result);
            }
        };

        window.addEventListener("unhandledrejection", (evt: PromiseRejectionEvent) =>
        {
            evt.preventDefault();

            const result = _handler.handle(evt.reason);
            if (result instanceof HandledException)
            {
                // eslint-disable-next-line no-console
                console.warn(result);
            }
        });
    }
};

export default errorsHandler;
